import styled from '@emotion/styled'
import { createPropFilter, WebLink, WebLinkProps } from '@news-mono/web-common'
import { FillProperty } from 'csstype'
import { Link } from 'react-router-dom'
import { IconChevronRight } from '../../icons/IconChevronRight/IconChevronRight'
import { IconChevronRightAlt } from '../../icons/IconChevronRightAlt/IconChevronRightAlt'
import { IconClockAlt } from '../../icons/IconClockAlt/IconClockAlt'
import { colors } from '../../__styling/settings/colors'
import { easing } from '../../__styling/settings/easing'
import { fonts } from '../../__styling/settings/fonts'
import { FontScales } from '../../__styling/settings/fontScale'
import { metrics, ThemeMargins } from '../../__styling/settings/metrics'
import { Section, sections } from '../../__styling/settings/sections'
import { PerthNowSection } from '../../__styling/settings/sections/perthnow'
import { zIndex } from '../../__styling/settings/z-index'
import { breakpoint, breakpointMax } from '../../__styling/style-functions'
import { calcEm } from '../../__styling/style-functions/calc-em'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import {
    textCrop,
    visuallyhidden,
    visuallyhiddenOff,
} from '../../__styling/style-mixins'
import { themedValue } from '../../__styling/themed-value'

export interface NavLink {
    text: string
    link: string
}

export interface SectionHeaderWrapper {
    hasMargin?: boolean
    canGrow?: boolean
    sectionOverride?: Section
    sponsor?: string
    masthead?: NavLink
    invisibleHeaderBreakpoint?: 'sm' | 'md'
    hideBackground?: boolean
    horizontalSpacingInWrapper?: keyof ThemeMargins
}

export interface SectionHeaderContainer {
    sponsor?: string
}

export const StyledSectionHeaderWrapper = styled('div')<SectionHeaderWrapper>(
    ({
        theme,
        sponsor,
        canGrow,
        invisibleHeaderBreakpoint,
        hasMargin,
        sectionOverride,
        hideBackground,
        horizontalSpacingInWrapper,
    }) => [
        {
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            flexWrap: 'wrap',
            flex: canGrow ? '1 0 auto' : '0 0 auto', // Flexgrow set to 0 so it wont stretch in containers that have stretch set
            marginBottom: hasMargin ? calcRem(theme.margins.md) : undefined,
        },
        invisibleHeaderBreakpoint !== undefined && {
            ...visuallyhidden,

            [breakpoint(invisibleHeaderBreakpoint || 'md')]: {
                ...visuallyhiddenOff,
                opacity: 0,
            },
        },

        horizontalSpacingInWrapper !== undefined && {
            marginLeft: calcRem(theme.margins[horizontalSpacingInWrapper]),
        },

        theme.kind === 'perthnow' &&
            !hideBackground && {
                minHeight: sponsor ? calcRem(54) : calcRem(36),
                padding: calcRem(7, metrics.perthnow.margins.sm),
                backgroundImage:
                    sections.perthnow[
                        (sectionOverride || theme.section) as PerthNowSection
                    ].primaryColor || sections.perthnow.default.primaryColor,
                backgroundColor:
                    sections.perthnow[
                        (sectionOverride || theme.section) as PerthNowSection
                    ].primaryColor || sections.perthnow.default.primaryColor,
            },
    ],
)

export const StyledSectionHeader = styled('div')<SectionHeaderContainer>(
    {
        display: 'flex',
        alignItems: 'center',
        alignContent: 'center',
        position: 'relative',
        flexGrow: 1,

        [breakpointMax('xs')]: {
            flexWrap: 'wrap',
        },
    },
    ({ theme }) => {
        return {
            color: themedValue(theme, {
                perthnow: colors.white,
                thewest: theme.colors.text.secondary,
                sevennews: colors.sevennews.bauhausBlack,
                fallback: colors.black,
            }),
        }
    },
    ({ theme, sponsor }) =>
        // Creates the block under the section header text
        theme.kind === 'thewest' && {
            '&::before': {
                content: `''`,
                position: 'absolute',
                bottom: sponsor ? -8 : 0,
                left: 0,
                width: 31, //The length of the AFL text so it doesn't look weird
                height: 3,
                backgroundColor: colors.black,
            },
        },
)

export const StyledIconClock = styled(IconClockAlt)(({ theme }) => ({
    position: 'relative',
    verticalAlign: 'middle',
    paddingRight: calcRem(theme.margins.xs),
    width: calcEm(22),
    height: calcEm(20),
    fill: themedValue(theme, {
        sevennews: (resolved) => resolved.sectionHeader.element.primaryColor,
        fallback: colors.sevennews.red,
    }) as FillProperty,
}))

export const StyledSevenNewsChevron = styled(IconChevronRightAlt)(
    ({ theme }) => ({
        position: 'relative',
        verticalAlign: 'middle',
        width: calcEm(18),
        height: calcEm(20),
        paddingRight: calcRem(theme.margins.xs),
        fill: themedValue(theme, {
            sevennews: (resolved) =>
                resolved.sectionHeader.element.primaryColor,
            fallback: colors.sevennews.red,
        }) as FillProperty,
    }),
)

export const StyledChevronRight = styled(IconChevronRight)(({ theme }) => ({
    position: 'relative',
    verticalAlign: 'middle',
    width: calcEm(16),
    height: calcEm(16),
    marginLeft: calcRem(theme.margins.xs),
    marginTop: calcRem(-3),
    fill: themedValue(theme, {
        perthnow: colors.white,
        fallback: theme.colors.text.secondary,
    }) as FillProperty,
}))
export interface StyledHeadingProps {
    isLink: boolean
    fontScale: FontScales | undefined
    showLogo?: boolean
}

export const StyledHeading = styled('h2')<StyledHeadingProps>(
    ({ theme, fontScale, showLogo }) => [
        {
            margin: 0,
            textTransform: 'uppercase',
            position: 'relative',
            fontFamily: theme.fonts.heading,
            fontWeight: themedValue(theme, {
                perthnow: 700,
                sevennews: 600,
                thewest: 400,
            }),
            fontSize: themedValue(theme, {
                thewest: fontScale
                    ? calcRem(Math.ceil(18 * fontScale))
                    : calcRem(16),
                fallback: fontScale
                    ? calcRem(Math.ceil(18 * fontScale))
                    : calcRem(22),
            }),
        },
        theme.kind === 'thewest' && {
            paddingBottom: calcRem(metrics.thewest.margins.sm),
            // Strips the extra spacing from above and below the text using pseudo-elements on the heading. Enabling perfect top and bottom margins.
            ...textCrop({
                fontSize: 24,
                desiredLineHeight: 1.2,
                cropLineHeight: 1.6,
            }),
        },
        theme.kind === 'sevennews' && {
            fontFamily: fonts.sevennews.headingAlternative,
            textTransform: 'none',
            alignSelf: 'flex-end',
            paddingLeft: showLogo ? calcRem(9) : undefined,
        },
        theme.kind === 'perthnow' && {
            lineHeight: 1,
        },
    ],
)

export interface StyledHeadingLinkProps {
    colorOverride?: string
}

export const StyledHeadingLink = styled(WebLink, {
    shouldForwardProp: createPropFilter<
        { colorOverride?: string } & WebLinkProps
    >()(['colorOverride']),
})<{ colorOverride?: string }>((props) => [
    {
        textDecoration: 'none',
        position: 'relative',
        zIndex: zIndex.common.link,

        '&:focus': {
            outline: 'none',
        },
    },
    props.theme.kind === 'thewest' && {
        display: 'flex',
        color: props.theme.colors.text.secondary,
        ...textCrop({
            fontSize: 24,
            desiredLineHeight: 1.21,
            cropLineHeight: 1.3,
        }),

        '&:focus': {
            textDecoration: 'underline',
        },
    },
    props.theme.kind === 'sevennews' && {
        display: 'inline-block',
        alignSelf: 'flex-end',
        color: themedValue(props.theme, {
            sevennews: (section) => section.sectionHeader.text.primaryColor,
            fallback: undefined,
        }),

        '&:focus': {
            outline: `solid 2px`,
            outlineColor: themedValue(props.theme, {
                sevennews: (section) => section.focusColor,
                fallback: undefined,
            }),
        },

        '&:hover h2': {
            textDecoration: 'underline',
            color: themedValue(props.theme, {
                sevennews: (section) => section.sectionHeader.text.hoverColor,
                fallback: undefined,
            }),
        },

        '&:hover svg': {
            fill: themedValue(props.theme, {
                sevennews: (section) =>
                    section.sectionHeader.element.hoverColor,
                fallback: undefined,
            }) as FillProperty,
        },
    },
    props.theme.kind === 'perthnow' && {
        color: colors.white,

        '&:focus': {
            textDecoration: 'underline',
        },

        // Creates a box behind the text for the hover state
        '&::before': {
            content: `''`,
            display: 'block',
            width: 'calc(100% + 12px)',
            height: calcRem(29),
            top: -4, // text offset
            left: -6,
            position: 'absolute',
            zIndex: zIndex.perthnow.linkHighlight,
            backgroundColor: 'none',
            transition: `background-color ${easing.easeOut.fast}`,
        },

        '&:hover::before': {
            backgroundColor: 'rgba(0,0,0,0.5)',
        },

        '&:active::before': {
            backgroundColor: 'rgba(255,255,255,0.2)',
        },
    },
    props.colorOverride && {
        color: props.colorOverride,
    },
])

export const StyledList = styled('ul')({
    display: 'none',

    [breakpoint('md')]: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
        width: '100%',
        flexDirection: 'row',
        flex: 1,
        justifyContent: 'flex-end',
        alignItems: 'center',
        display: 'flex',
        textTransform: 'uppercase',
    },
})

export interface StyledListItemProps {
    classNames?: string
    section: string
}

export const StyledListItem = styled('li')<StyledListItemProps>(
    {
        position: 'relative',
        width: 'auto',
        flex: '0 1 auto',
        textAlign: 'center',
        textTransform: 'uppercase',

        '&:last-child': {
            borderRight: 'none',
        },

        '&::after': {
            content: "'\\007c'",
            display: 'block',
            position: 'absolute',
            left: 'calc(100% - 2px)',
            zIndex: 1,
            lineHeight: 1,
        },

        '&:last-of-type::after': {
            content: 'none',
        },

        '&:last-of-type a': {
            marginRight: 0,
        },
    },
    ({ theme }) => ({
        fontSize: themedValue(theme, {
            perthnow: calcRem(12),
            fallback: calcRem(16),
        }),
        fontFamily: theme.fonts.sansSerif,

        // Separator
        '&::after': {
            color: themedValue(theme, {
                thewest: colors.thewest.greyDolphin,
                perthnow: colors.white,
                sevennews: colors.sevennews.stormGrey,
            }),
            top: themedValue(theme, {
                perthnow: 2,
                fallback: 0,
            }),
        },
    }),
)

export const StyledLink = styled(Link)(
    {
        display: 'block',
        whiteSpace: 'nowrap',
        position: 'relative',
        fontWeight: 500,
        textDecoration: 'none',
        height: '100%',
        margin: `0 ${calcRem(metrics.perthnow.margins.sm)}`,
        zIndex: zIndex.common.link,

        '&:focus': {
            textDecoration: 'underline',
            outline: 'none',
        },
    },
    (props) => [
        {
            fontSize: themedValue(props.theme, {
                thewest: calcRem(14),
                sevennews: calcRem(16),
                perthnow: undefined,
            }),
            lineHeight: themedValue(props.theme, {
                perthnow: undefined,
                fallback: 1,
            }),
            color: themedValue(props.theme, {
                thewest: colors.thewest.greyGorilla,
                sevennews: colors.sevennews.stormGrey,
                perthnow: colors.white,
            }),
            '&:hover': {
                color: themedValue(props.theme, {
                    thewest: props.theme.sectionValues.primaryColor,
                    perthnow: undefined,
                    sevennews: colors.sevennews.red,
                }),
            },
        },
        props.theme.kind === 'perthnow' && {
            '&::before': {
                content: `''`,
                display: 'block',
                width: 'calc(100% + 12px)',
                height: calcRem(24),
                top: -metrics.perthnow.baselineOffset,
                left: -6,
                position: 'absolute',
                zIndex: zIndex.perthnow.linkHighlight,
                backgroundColor: 'none',
                transition: `background-color ${easing.easeOut.fast}`,
            },

            '&:hover::before': {
                backgroundColor: 'rgba(0,0,0,0.5)',
            },

            '&:active::before': {
                backgroundColor: 'rgba(255,255,255,0.2)',
            },
        },
    ],
)

export const StyledWebLink = StyledLink.withComponent(WebLink)

export const StyledMastheadLink = styled(Link)(({ theme }) => [
    {
        fontWeight: 700,
        textDecoration: 'none',
        position: 'relative',
        textTransform: 'none',
        lineHeight: 1,
        marginLeft: 'auto',
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.white,
            sevennews: colors.sevennews.stormGrey,
        }),
        fontFamily: themedValue(theme, {
            thewest: fonts.thewest.serif,
            perthnow: fonts.perthnow.serif,
            sevennews: fonts.sevennews.sansSerif,
        }),
        zIndex: zIndex.common.link,

        '&:focus': {
            textDecoration: 'underline',
            outline: 'none',
        },
    },
    theme.kind === 'perthnow' && {
        '&::before': {
            content: `''`,
            display: 'block',
            width: 'calc(100% + 12px)',
            height: calcRem(24),
            left: -6,
            top: -4,
            position: 'absolute',
            zIndex: zIndex.perthnow.linkHighlight,
            backgroundColor: 'none',
            transition: `background-color ${easing.easeOut.fast}`,
        },

        '&:hover::before': {
            backgroundColor: 'rgba(0,0,0,0.5)',
        },

        '&:active::before': {
            backgroundColor: 'rgba(255,255,255,0.2)',
        },
    },
])

export const StyledMastheadContainer = styled('div')({
    lineHeight: 1,

    [breakpointMax('xs')]: {
        paddingTop: calcRem(9),
        flexBasis: '100%',
    },
})

export const StyledImageContainer = styled('div')<{
    customMargin?: string
    hideOnMobile?: boolean
}>(({ customMargin, hideOnMobile }) => ({
    lineHeight: 1,
    display: hideOnMobile ? 'none' : undefined, // TODO hide on mobile on perth now

    [breakpoint('xs')]: {
        display: 'block',
        marginLeft: customMargin ? customMargin : 'auto',
    },

    [breakpointMax('xs')]: {
        paddingTop: calcRem(9),
        flexBasis: '100%',
    },
}))
